import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useOCPP } from '../contexts/OCPPContext';
import { useToast } from '../contexts/ToastContext';
import { formatConnectorSoc } from '../utils/format';

// Live Heartbeat Timer Component
const LiveHeartbeatTimer = ({ lastHeartbeat, connected }) => {
  const [secondsAgo, setSecondsAgo] = useState(0);

  useEffect(() => {
    if (!connected || !lastHeartbeat) {
      setSecondsAgo(0);
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const heartbeatTime = new Date(lastHeartbeat);
      const diffInSeconds = Math.floor((now - heartbeatTime) / 1000);
      setSecondsAgo(diffInSeconds);
    };

    // Update immediately
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [lastHeartbeat, connected]);

  if (!connected || !lastHeartbeat) return null;

  const formatTimeAgo = (seconds) => {
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s ago`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m ago`;
  };

  const getTimerColor = (seconds) => {
    if (seconds < 15) return '#4caf50'; // Green - healthy
    if (seconds < 30) return '#ff9800'; // Orange - warning
    return '#f44336'; // Red - concerning
  };

  return (
    <div style={{ 
      fontSize: '0.6rem', 
      color: getTimerColor(secondsAgo), 
      marginTop: '2px',
      fontWeight: '500',
      textAlign: 'right',
      lineHeight: '1'
    }}>
      ⏱️ {formatTimeAgo(secondsAgo)}
    </div>
  );
};

const ChargerCard = ({ charger }) => {
  const { emergencyStop } = useOCPP();
  const { addToast } = useToast();
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  


  const handleEmergencyStop = async (method = 'zero_power') => {
    if (!window.confirm(`Are you sure you want to execute emergency stop for ${charger.id}?`)) {
      return;
    }

    setEmergencyLoading(true);
    try {
      await emergencyStop(charger.id, method);
      addToast('Emergency stop executed successfully', 'success');
    } catch (error) {
      addToast(`Emergency stop failed: ${error.message}`, 'error');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Connected': return 'status-connected';
      case 'Disconnected': return 'status-disconnected';
      default: return 'status-disconnected';
    }
  };

  const getConnectorStatusColor = (status) => {
    switch (status) {
      case 'Available': return 'status-available';
      case 'Occupied': return 'status-charging';
      case 'Charging': return 'status-charging';
      case 'Faulted': return 'status-disconnected';
      default: return 'status-available';
    }
  };

  const formatPower = (watts) => {
    if (watts == null || watts === '' || Number(watts) === 0) return '0 W';
    if (watts >= 1000) {
      return `${(watts / 1000).toFixed(1)} kW`;
    }
    return `${watts} W`;
  };

  const connectors = charger.connectors || [];
  
  // Get all active transactions from charger.active_transactions
  const activeTransactions = charger.active_transactions || {};
  const activeTransactionEntries = Object.entries(activeTransactions);

  // Truncate long charger IDs for display
  const truncateChargerId = (id, maxLength = 20) => {
    if (!id) return '';
    if (id.length <= maxLength) return id;
    return id.substring(0, maxLength) + '...';
  };

  return (
    <div className="charger-card">
      <div className="charger-header" style={{ marginBottom: '1rem' }}>
        <div style={{ minWidth: 0, flex: '1 1 auto', marginRight: '1rem' }}>
          <div className="charger-id" style={{
            marginBottom: '0.25rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            cursor: charger.id.length > 20 ? 'help' : 'default'
          }} title={charger.id.length > 20 ? `Full ID: ${charger.id}` : charger.id}>
            {charger.display_name || truncateChargerId(charger.id)}
          </div>
          {charger.display_name && (
            <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.25rem' }}>
              {truncateChargerId(charger.id)}
            </div>
          )}
          <div style={{ 
            fontSize: '0.9rem', 
            color: '#666', 
            marginBottom: '0.25rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {charger.vendor} {charger.model}
          </div>
          {charger.firmware_version && (
            <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.25rem' }}>
              FW: {charger.firmware_version}
            </div>
          )}
        </div>
        <div className="charger-status" style={{ flex: '0 0 auto', minWidth: '120px' }}>
          <span className={`status-indicator ${getStatusColor(charger.connection_status)}`} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ fontWeight: '500', fontSize: '0.85rem' }}>
              {charger.connection_status}
            </div>
            {charger.last_heartbeat && charger.connection_status === 'Connected' && (
              <div style={{ 
                fontSize: '0.65rem', 
                color: '#666', 
                marginTop: '2px',
                textAlign: 'right'
              }}>
                {new Date(charger.last_heartbeat).toLocaleTimeString()}
              </div>
            )}
            <LiveHeartbeatTimer 
              lastHeartbeat={charger.last_heartbeat}
              connected={charger.connection_status === 'Connected'}
            />
          </div>
        </div>
      </div>

      <div className="charger-connectors" style={{ marginTop: '1.5rem' }}>
        <div style={{ marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #eee' }}>
          <strong>Connectors ({connectors.length})</strong>
        </div>
        
        {connectors.length === 0 ? (
          <div style={{ color: '#666', fontStyle: 'italic' }}>
            {charger.connection_status === 'Connected' ? 'No connector data yet' : 'Offline - no data'}
          </div>
        ) : (
          connectors.map((connector, index) => (
            <div key={connector.id || index} className="connector" style={{ 
              marginBottom: '1rem', 
              padding: '0.75rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef'
            }}>
              {/* Header: compact connector name and status */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <span className={`status-indicator ${getConnectorStatusColor(connector.status)}`} />
                <span style={{ fontWeight: '600', marginLeft: '0.4rem', fontSize: '0.85rem' }}>
                  C{connector.id}
                </span>
                <div style={{ 
                  fontSize: '0.7rem', 
                  fontWeight: '600',
                  color: connector.status === 'Charging' ? '#28a745' : '#6c757d',
                  backgroundColor: connector.status === 'Charging' ? '#d4edda' : '#e9ecef',
                  padding: '0.15rem 0.35rem',
                  borderRadius: '8px',
                  marginLeft: '0.5rem'
                }}>
                  {connector.status}
                </div>
              </div>
              {(connector.vehicle?.identifier || connector.id_tag) && (
                <div style={{ fontSize: '0.8rem', marginBottom: '0.4rem', wordBreak: 'break-all' }}>
                  <strong>ID:</strong> {connector.vehicle?.identifier || connector.id_tag}
                  {connector.vehicle?.kind ? ` (${connector.vehicle.kind})` : ''}
                </div>
              )}
              
              {/* Power info */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem' }}>
                  Max: {connector.max_power ? formatPower(connector.max_power) : '—'} | Current: {formatPower(connector.current_power || 0)}
                  {formatConnectorSoc(connector) ? ` | SoC ${formatConnectorSoc(connector)}` : ''}
                </div>
                
                {/* Utilization bar */}
                {connector.max_power > 0 && (
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '0.25rem'
                    }}>
                      <span style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                        Utilization
                      </span>
                      <span style={{ fontSize: '0.7rem', fontWeight: '600', color: '#495057' }}>
                        {Math.round((connector.current_power / connector.max_power) * 100)}%
                      </span>
                    </div>
                    <div style={{ 
                      width: '100%', 
                      height: '6px', 
                      backgroundColor: '#e9ecef',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{ 
                        width: `${Math.min((connector.current_power / connector.max_power) * 100, 100)}%`,
                        height: '100%',
                        backgroundColor: connector.current_power > 0 ? 
                          (connector.current_power / connector.max_power > 0.8 ? '#dc3545' : '#fd7e14') : '#28a745',
                        borderRadius: '3px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Error display */}
              {connector.error_code && connector.error_code !== 'NoError' && (
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: '#721c24', 
                  backgroundColor: '#f8d7da',
                  padding: '0.4rem',
                  borderRadius: '4px',
                  border: '1px solid #f5c6cb',
                  fontWeight: '500'
                }}>
                  ⚠️ {connector.error_code}
                </div>
              )}
            </div>
          ))
        )}

        {activeTransactionEntries.length > 0 && (
          <div style={{ 
            background: '#e3f2fd', 
            padding: '1rem', 
            borderRadius: '6px', 
            marginTop: '1.5rem',
            fontSize: '0.9rem',
            border: '1px solid #bbdefb'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#1976d2' }}>
              ⚡ Active Transaction{activeTransactionEntries.length > 1 ? 's' : ''} ({activeTransactionEntries.length})
            </div>
            {activeTransactionEntries.map(([connectorId, transaction]) => (
              <div key={connectorId} style={{ 
                marginBottom: activeTransactionEntries.length > 1 ? '0.75rem' : '0',
                paddingBottom: activeTransactionEntries.length > 1 ? '0.75rem' : '0',
                borderBottom: activeTransactionEntries.length > 1 ? '1px solid #bbdefb' : 'none'
              }}>
                <div style={{ fontWeight: '500', marginBottom: '0.25rem', color: '#1565c0' }}>
                  Connector {connectorId} (ID: {transaction.transaction_id})
                </div>
                <div style={{ marginBottom: '0.25rem' }}>
                  <strong>ID Tag:</strong> {transaction.id_tag || 'N/A'}
                </div>
                <div>
                  <strong>Started:</strong> {transaction.start_time ? 
                    new Date(transaction.start_time).toLocaleString() : 'N/A'}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
          <Link 
            to={`/charger/${encodeURIComponent(charger.id)}`} 
            className="btn btn-primary"
            style={{ textDecoration: 'none', flex: 1, textAlign: 'center' }}
          >
            Details
          </Link>
          <button
            onClick={() => handleEmergencyStop('stop_transaction')}
            disabled={emergencyLoading || charger.connection_status !== 'Connected'}
            className="emergency-button"
            style={{ flex: 1 }}
            title="Stop all active charging transactions"
          >
            {emergencyLoading ? 'Stopping...' : 'Stop Charging'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChargerCard;